'use client';

import { Branch } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useEffect, useState } from 'react';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';

import { FindBranchesNearYou } from './find-branches-near-you.component';
import { NearbyBranchesSectionLoading } from './nearby-branches-section-loading.component';

import { AnalyticsBranchPageEnum } from '@/app/(main)/(pages)/_enums/analytics-branch-page.enum';
import { getBranchesNearYou } from '@/app/(main)/(pages)/unidades-perto-de-voce/_services/get-branches-near-you.service';
import { ContentContainer } from '@/components/ui/content-container.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { BranchesList } from '@/app/(main)/(pages)/_components/branches-section/branches-list/branches-list.component';
import { getUserLocationStorage } from '@/storage/location.storage';
import { getCitySlug } from '@/app/(main)/(pages)/_services/get-city-slug.service';
import { Button } from '@/components/ui/button/button.component';
import { setOpenLocationSelectionModal } from '@/store/modal';
import { Skeleton } from '@/components/ui/skeleton.component';

export const NearbyBranchesSection = () => {
  const [location, setLocation] = useState(() => getUserLocationStorage());
  const [branches, setBranches] = useState<Branch[]>([]);
  const [citySlug, setCitySlug] = useState<string>('');
  const [isLoading, setLoading] = useState(true);
  const noLocation = !location?.city || !location?.state;
  const hasBranches = branches.length > 0;

  useEffect(() => {
    const fetchCity = async () => {
      if (noLocation) {
        return null;
      }

      const city = await getCitySlug(location.city, location.state);

      if (city?.slug) {
        setCitySlug(city.slug);
      }
    };

    fetchCity();
  }, [location?.city, location?.state, noLocation]);

  useEffect(() => {
    const fetchBranches = async () => {
      setLoading(true);

      if (location?.city && location.state) {
        const branchesResponse = await getBranchesNearYou(
          location.city,
          location.state,
        );

        const branches = (branchesResponse?.branchesByCity || []).filter(
          (branch): branch is Branch => branch !== null,
        );

        setBranches(branches);
      } else {
        setBranches([]);
      }
      setLoading(false);
    };

    fetchBranches();
  }, [location]);

  const handleSelect = (): void => {
    const savedLocation = getUserLocationStorage();
    setLocation(savedLocation!);
  };

  return (
    <section data-testid={DataTestIdEnum.CITY_BRANCH_LIST_SECTION}>
      <ContentContainer className="py-6">
        <Typography
          variant={TypographyVariant.LARGE_SUBTITLE}
          className="text-start text-2xl font-bold mb-2"
        >
          Unidades Físicas
        </Typography>
        {isLoading && <Skeleton className="w-32 h-6 rounded" />}

        {!isLoading && location?.city && location.state && hasBranches && (
          <Button
            variant="text"
            className="w-auto"
            startIcon={faLocationDot}
            onClick={() =>
              setOpenLocationSelectionModal(true, {
                locationCallback: handleSelect,
              })}
          >
            {`${location.city} - ${location.state}`}
          </Button>
        )}
      </ContentContainer>
      <ContentContainer className="!px-0">
        {isLoading && <NearbyBranchesSectionLoading />}

        {(noLocation || !hasBranches) && !isLoading && <FindBranchesNearYou onClickLocationCallback={handleSelect} /> }

        {branches && (
          <BranchesList
            citySlug={citySlug}
            branches={branches}
            pageFrom={AnalyticsBranchPageEnum.BRANCH_PAGE}
          />
        )}
      </ContentContainer>
    </section>
  );
};
